import type { UseAccordionProps } from '@pearlconvert/ui/components/accordion/accordion.types'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { arrayEqual } from '@maestro/commerce-blocks/utils/array-equal.utils'

const normalizeArray = (v: string[] | undefined): string[] =>
  Array.isArray(v) ? v : []

export const useAccordion = ({
  canOpenMultiple,
  onToggle,
  initialActiveIds,
}: UseAccordionProps) => {
  const normalizedInitial = useMemo(() => {
    return normalizeArray(initialActiveIds)
  }, [initialActiveIds])

  const [isInitial, setIsInitial] = useState(true)
  const [activeIds, setActiveIds] = useState<string[]>(normalizedInitial)

  const prevInitialActiveIdsRef = useRef<string[]>(normalizedInitial)

  useEffect(() => {
    const prev = prevInitialActiveIdsRef.current
    const changed = !arrayEqual(prev, normalizedInitial)

    if (changed || isInitial) {
      setActiveIds(normalizedInitial)
      setIsInitial(true)
      prevInitialActiveIdsRef.current = normalizedInitial
    }
    // don't add isInitial in deps to avoid loop after setIsInitial.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedInitial])

  const handleChangeActive = useCallback(
    (id: string) => {
      const isOpening = !activeIds.includes(id)
      onToggle?.(id, isOpening)
      setIsInitial(false)

      setActiveIds((prev) => {
        const isActive = prev.includes(id)

        if (canOpenMultiple) {
          return isActive ? prev.filter((x) => x !== id) : [...prev, id]
        }

        return isActive ? [] : [id]
      })
    },
    [activeIds, canOpenMultiple, onToggle]
  )

  return {
    activeIds,
    isInitial,
    handleChangeActive,
  }
}
